import React from 'react';
import { AssetType } from 'config/consts';

export type AssetTypeKey = typeof AssetType[keyof typeof AssetType];

const foreignAssetTypeList = [
  AssetType['外国株式'],
  AssetType['外国ETF・ETN'],
  AssetType['外国REIT'],
  AssetType['外国債券'],
  AssetType['外国EB'],
] as AssetTypeKey[];

export const isDomestic = (assetType: AssetTypeKey) => {
  return !foreignAssetTypeList.includes(assetType);
};
export const isForeign = (assetType: AssetTypeKey) => {
  return foreignAssetTypeList.includes(assetType);
};

// memo: 銘柄詳細に遷移させない商品種別
const notExistDetailTypes = [
  AssetType['外国株式'],
  AssetType['外国ETF・ETN'],
  AssetType['外国REIT'],
  AssetType['国内債券'],
  AssetType['外国債券'],
  AssetType['国内EB'],
  AssetType['外国EB'],
  AssetType['不動産'],
  AssetType['未上場株式'],
  AssetType['ロボアドバイザー'],
  AssetType['預金_MRF'],
  AssetType['その他'],
] as number[];
export const isNotExistDetailAssetTypes = (type: number) => {
  return notExistDetailTypes.includes(type);
};

export const getQuantityUnit = (key: AssetTypeKey): string => {
  switch (key) {
    case AssetType['国内現物']:
    case AssetType['外国株式']:
    case AssetType['未上場株式']:
    case AssetType['ロボアドバイザー']:
    case AssetType['信用']:
      return '株';
    case AssetType['投資信託']:
    case AssetType['国内ETF・ETN']:
    case AssetType['外国ETF・ETN']:
    case AssetType['国内REIT']:
    case AssetType['外国REIT']:
      return '口';
    default:
      return '';
  }
};

// memo: 数量項目を非表示にする商品種別
const qtyHiddenAssetTypeList = [
  AssetType['国内債券'],
  AssetType['外国債券'],
  AssetType['国内EB'],
  AssetType['外国EB'],
  AssetType['不動産'],
  AssetType['預金_MRF'],
] as number[];
export const isQtyHiddenAssetType = (key: AssetTypeKey): boolean => {
  return qtyHiddenAssetTypeList.includes(key);
};

export const assetTypeKeyToString = (
  key: AssetTypeKey,
  isRoboShortLabel = false
): string => {
  switch (key) {
    case AssetType['国内現物']:
      return '国内株式';
    case AssetType['信用']:
      return '信用取引';
    case AssetType['投資信託']:
      return '投資信託';
    case AssetType['ロボアドバイザー']:
      return isRoboShortLabel ? 'ロボアド' : 'ロボアドバイザー';
    case AssetType['預金_MRF']:
      return '預金・MRF';
    case AssetType['その他']:
      return 'その他';
    case AssetType['外国株式']:
      return '外国株式';
    case AssetType['未上場株式']:
      return '未上場株式';
    case AssetType['国内ETF・ETN']:
      return '国内ETF・ETN';
    case AssetType['外国ETF・ETN']:
      return '外国ETF・ETN';
    case AssetType['国内REIT']:
      return '国内REIT';
    case AssetType['外国REIT']:
      return '外国REIT';
    case AssetType['国内債券']:
      return '国内債券';
    case AssetType['外国債券']:
      return '外国債券';
    case AssetType['国内EB']:
      return '国内EB';
    case AssetType['外国EB']:
      return '外国EB';
    case AssetType['不動産']:
      return '不動産';
  }
  return '';
};

export const assetTypeKeyToStringLabel = (key: AssetTypeKey): string => {
  switch (key) {
    case AssetType['国内現物']:
      return '国内株';
    case AssetType['信用']:
      return '信用取引';
    case AssetType['投資信託']:
      return '投信';
    case AssetType['ロボアドバイザー']:
      return 'ロボアド';
    case AssetType['預金_MRF']:
      return '預金';
    case AssetType['その他']:
      return 'その他';
    case AssetType['外国株式']:
      return '外国株';
    case AssetType['未上場株式']:
      return '未上場株式';
    case AssetType['国内ETF・ETN']:
      return '国内ETF・ETN';
    case AssetType['外国ETF・ETN']:
      return '外国ETF・ETN';
    case AssetType['国内REIT']:
      return '国内REIT';
    case AssetType['外国REIT']:
      return '外国REIT';
    case AssetType['国内債券']:
      return '国内債券';
    case AssetType['外国債券']:
      return '外国債券';
    case AssetType['国内EB']:
      return '国内EB';
    case AssetType['外国EB']:
      return '外国EB';
    case AssetType['不動産']:
      return '不動産';
  }
  return '';
};

export const assetTypeKeyToColor = (key: AssetTypeKey): string => {
  switch (key) {
    case AssetType['国内現物']:
      return 'bg-dot-Stock';
    case AssetType['信用']:
      return 'bg-dot-Margin';
    case AssetType['投資信託']:
      return 'bg-dot-InvestmentTrust';
    case AssetType['ロボアドバイザー']:
      return 'bg-dot-Robo';
    case AssetType['預金_MRF']:
      return 'bg-dot-Cash';
    case AssetType['その他']:
      return 'bg-dot-Other';
  }
  return '';
};

export const assetTypeKeyToColorCode = (key: AssetTypeKey): string => {
  switch (key) {
    case AssetType['国内現物']:
      return '#179a8d';
    case AssetType['信用']:
      return '#7ad8c6';
    case AssetType['ロボアドバイザー']:
      return '#f47f37';
    case AssetType['預金_MRF']:
      return '#9f3bf4';
    case AssetType['その他']:
      return '#d2deef';
    case AssetType['外国株式']:
      return '#3fa9f5';
    case AssetType['投資信託']:
      return '#dd80d4';
    case AssetType['未上場株式']:
      return '#8C8C8C';
    case AssetType['国内ETF・ETN']:
      return '#6D71DA';
    case AssetType['外国ETF・ETN']:
      return '#17CECE';
    case AssetType['国内REIT']:
      return '#FE5F5F';
    case AssetType['外国REIT']:
      return '#D09494';
    case AssetType['国内債券']:
      return '#26E016';
    case AssetType['外国債券']:
      return '#F0C22B';
    case AssetType['国内EB']:
      return '#4098B4';
    case AssetType['外国EB']:
      return '#91BEAB';
    case AssetType['不動産']:
      return '#FF58DA';
  }
  return '';
};

export const assetTypeKeyToDotColor = (assetTypeKey: AssetTypeKey): string => {
  switch (assetTypeKey) {
    case AssetType['国内現物']:
      return 'bg-dot-Stock';
    case AssetType['信用']:
      return 'bg-dot-Margin';
    case AssetType['ロボアドバイザー']:
      return 'bg-dot-Robo';
    case AssetType['預金_MRF']:
      return 'bg-dot-Cash';
    case AssetType['その他']:
      return 'bg-dot-Other';
    case AssetType['外国株式']:
      return 'bg-dot-ForeignStock';
    case AssetType['投資信託']:
      return 'bg-dot-InvestmentTrust';
    case AssetType['未上場株式']:
      return 'bg-dot-UnlistedStocks';
    case AssetType['国内ETF・ETN']:
      return 'bg-dot-EtfEtn';
    case AssetType['外国ETF・ETN']:
      return 'bg-dot-ForeignEtfEtn';
    case AssetType['国内REIT']:
      return 'bg-dot-Reit';
    case AssetType['外国REIT']:
      return 'bg-dot-ForeignReit';
    case AssetType['国内債券']:
      return 'bg-dot-Bond';
    case AssetType['外国債券']:
      return 'bg-dot-ForeignBond';
    case AssetType['国内EB']:
      return 'bg-dot-Eb';
    case AssetType['外国EB']:
      return 'bg-dot-ForeignEb';
    case AssetType['不動産']:
      return 'bg-dot-RealEstate';
  }
  return '';
};

// チャートではクラスで色を指定できないのでべたがき
export const assetTypeKeyToDotColorString = (key: AssetTypeKey): string => {
  switch (key) {
    case AssetType['国内現物']:
      return '#179a8d';
    case AssetType['信用']:
      return '#7ad8c6';
    case AssetType['ロボアドバイザー']:
      return '#f47f37';
    case AssetType['投資信託']:
      return '#dd80d4';
    case AssetType['預金_MRF']:
      return '#9f3bf4';
    case AssetType['その他']:
      return '#d2deef';
    case AssetType['外国株式']:
      return '#3fa9f5';
  }
  return '';
};

export const AssetTypesLabel = ({
  assetType,
  className = '',
}: {
  assetType: AssetTypeKey;
  className?: string;
}) => {
  const str = assetTypeKeyToStringLabel(assetType);
  const labelColor = assetTypeKeyToDotColor(assetType);
  return (
    <>
      <div
        className={`text-white text-1.5xs px-3 text-center justify-center flex items-center h-4 rounded-full ${className} ${labelColor}`}>
        {str}
      </div>
    </>
  );
};
